import React, { useState } from 'react';
import type {
  SlackReplyLanguage,
  SlackReplyLength,
  SlackReplyTone,
  SlackVoiceSettings,
  UserSettingsPublic,
  UserSettingsUpdate,
} from '../../services/clients/types';
import { Button } from '../../ui/common/Button';
import { RichTextComposer } from '../../ui/common/RichTextComposer';
import { SlackRealtimeInfoModal } from './SlackRealtimeInfoModal';
import { ProviderSetupGuideLink } from './ProviderSetupGuideModal';
import {
  buttonRowStyle,
  fieldGridStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  sectionStyle,
} from './settingsStyles';

export interface SettingsSlackSectionProps {
  settings: UserSettingsPublic;
  saving: boolean;
  slackTokenDraft: string;
  voiceDraft: SlackVoiceSettings;
  savedVoicePrompt: string;
  onSlackTokenDraft: (value: string) => void;
  onVoiceDraft: (value: SlackVoiceSettings) => void;
  onAutoSaveVoiceSelects: (value: SlackVoiceSettings) => void;
  onSavePatch: (
    patch: UserSettingsUpdate,
    successMessage: string,
    after?: () => void,
  ) => Promise<void>;
  onCancelNotes: () => void;
}

export const SettingsSlackSection: React.FC<SettingsSlackSectionProps> = ({
  settings,
  saving,
  slackTokenDraft,
  voiceDraft,
  savedVoicePrompt,
  onSlackTokenDraft,
  onVoiceDraft,
  onAutoSaveVoiceSelects,
  onSavePatch,
  onCancelNotes,
}) => {
  const [realtimeInfoOpen, setRealtimeInfoOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const slackStatus = settings.secrets.slackUserToken;
  const hasSlackDraft = Boolean(slackTokenDraft.trim());
  const notesDirty = voiceDraft.customPrompt.trim() !== savedVoicePrompt.trim();

  return (
    <section style={{ ...sectionStyle, borderBottom: 'none' }} data-testid="settings-slack-section">
      <p style={hintStyle}>
        Token de usuario para leer hilos y enviar respuestas. La voz guía los borradores de IA.
      </p>
      {!slackStatus?.configured ? (
        <p
          data-testid="settings-slack-not-configured"
          style={{ ...hintStyle, color: 'var(--z-text-2)' }}
        >
          Guarda un token de usuario para verificar la conexión y empezar a leer tus hilos.
        </p>
      ) : null}
      <div
        data-testid="settings-slack-token-setup"
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <input
          data-testid="settings-slack-token"
          type="password"
          autoComplete="off"
          placeholder={
            slackStatus?.configured
              ? 'Pega un nuevo token de usuario para reemplazarlo'
              : 'Pega tu token de usuario de Slack (xoxp-…)'
          }
          value={slackTokenDraft}
          onChange={(e) => onSlackTokenDraft(e.target.value)}
          style={inputStyle}
        />
        <ProviderSetupGuideLink source="slack" />
        <div style={buttonRowStyle}>
          {hasSlackDraft ? (
            <Button
              size="sm"
              variant="primary"
              data-testid="settings-slack-save"
              disabled={saving}
              loading={saving}
              onClick={() =>
                void onSavePatch(
                  { slackUserToken: slackTokenDraft.trim() },
                  'Token de Slack actualizado',
                  () => onSlackTokenDraft(''),
                )
              }
            >
              Guardar token
            </Button>
          ) : null}
          {slackStatus?.configured ? (
            <Button
              size="sm"
              variant="ghost"
              data-testid="settings-slack-clear"
              disabled={saving}
              onClick={() =>
                void onSavePatch(
                  { clearSlackUserToken: true },
                  'Token quitado (se usa el fallback de deploy)',
                )
              }
            >
              Eliminar token
            </Button>
          ) : null}
          {!hasSlackDraft && !slackStatus?.configured ? (
            <span style={hintStyle}>Sin token de usuario guardado.</span>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        data-testid="settings-slack-advanced-toggle"
        onClick={() => setAdvancedOpen((open) => !open)}
        style={{
          alignSelf: 'flex-start',
          border: 0,
          padding: 0,
          background: 'transparent',
          color: 'var(--z-text-2)',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {advancedOpen ? 'Ocultar configuración avanzada' : 'Mostrar configuración avanzada'}
      </button>
      {advancedOpen ? (
        <>
          <button
            data-testid="settings-slack-realtime-info"
            type="button"
            onClick={() => setRealtimeInfoOpen(true)}
            style={{
              alignSelf: 'flex-start',
              border: 0,
              padding: 0,
              background: 'transparent',
              color: 'var(--z-text-2)',
              cursor: 'pointer',
              font: 'inherit',
              fontSize: 12,
            }}
          >
            ⓘ Configuración de tiempo real (opcional)
          </button>
          <SlackRealtimeInfoModal
            isOpen={realtimeInfoOpen}
            onClose={() => setRealtimeInfoOpen(false)}
          />
          <div style={{ ...labelStyle, marginTop: 8 }} data-testid="settings-voice-section">
            Voz de las respuestas
          </div>
          <p style={hintStyle}>
            Idioma, tono y longitud guían los borradores. Las notas de estilo son markdown ligero.
          </p>
          <div className="z-settings-field-grid" style={fieldGridStyle}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>Idioma</span>
              <select
                data-testid="settings-voice-language"
                value={voiceDraft.language}
                onChange={(e) => {
                  const next = {
                    ...voiceDraft,
                    language: e.target.value as SlackReplyLanguage,
                  };
                  onVoiceDraft(next);
                  void onAutoSaveVoiceSelects(next);
                }}
                style={inputStyle}
              >
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>Tono</span>
              <select
                data-testid="settings-voice-tone"
                value={voiceDraft.tone}
                onChange={(e) => {
                  const next = {
                    ...voiceDraft,
                    tone: e.target.value as SlackReplyTone,
                  };
                  onVoiceDraft(next);
                  void onAutoSaveVoiceSelects(next);
                }}
                style={inputStyle}
              >
                <option value="professional">Profesional</option>
                <option value="casual">Casual</option>
                <option value="direct">Directo</option>
                <option value="warm">Cálido</option>
              </select>
            </label>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: '1 / -1' }}
            >
              <span style={labelStyle}>Longitud</span>
              <select
                data-testid="settings-voice-length"
                value={voiceDraft.replyLength}
                onChange={(e) => {
                  const next = {
                    ...voiceDraft,
                    replyLength: e.target.value as SlackReplyLength,
                  };
                  onVoiceDraft(next);
                  void onAutoSaveVoiceSelects(next);
                }}
                style={inputStyle}
              >
                <option value="short">Corta (~60 palabras)</option>
                <option value="medium">Media (~120 palabras)</option>
                <option value="long">Larga (~220 palabras)</option>
              </select>
            </label>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={labelStyle}>Notas de estilo</span>
            <p style={{ ...hintStyle, margin: 0 }} data-testid="settings-voice-prompt-hint">
              Editor enriquecido estilo Slack (barra B / I / S, listas, código). Atajos como en
              Slack: ⌘/Ctrl+B, I, Shift+X, Shift+C, Shift+7/8/9. Usa <strong>Aa</strong> para
              mostrar u ocultar el formato. Enter sigue siendo multilínea (sin enviar). Se guarda
              como markdown.
            </p>
            <RichTextComposer
              data-testid="settings-voice-prompt"
              value={voiceDraft.customPrompt}
              onChange={(markdown) => onVoiceDraft({ ...voiceDraft, customPrompt: markdown })}
              placeholder="Notas de estilo opcionales…"
              minHeight={110}
              toolbarDefaultVisible
              ariaLabel="Notas de estilo para borradores de Slack"
            />
          </div>

          <div style={buttonRowStyle}>
            {notesDirty ? (
              <>
                <Button
                  size="sm"
                  variant="primary"
                  data-testid="settings-voice-save"
                  disabled={saving}
                  loading={saving}
                  onClick={() =>
                    void onSavePatch(
                      {
                        slackVoice: {
                          language: voiceDraft.language,
                          tone: voiceDraft.tone,
                          replyLength: voiceDraft.replyLength,
                          customPrompt: voiceDraft.customPrompt,
                        },
                      },
                      'Notas de estilo guardadas',
                    )
                  }
                >
                  Guardar notas
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="settings-voice-cancel"
                  disabled={saving}
                  onClick={onCancelNotes}
                >
                  Cancelar
                </Button>
              </>
            ) : (
              <span style={hintStyle} data-testid="settings-voice-notes-clean">
                Notas al día
              </span>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
};
